<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

const FIRST_PAGE_SIZE = 10;
const OTHER_PAGE_SIZE = 12;

export default {
    name: "CPTemp8",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        projectPages() {
            const pages = [];

            if (this.projects && this.projects.projects) {
                const list = this.projects.projects;

                pages.push(list.slice(0, FIRST_PAGE_SIZE));

                for (let i = FIRST_PAGE_SIZE; i < list.length; i += OTHER_PAGE_SIZE) {
                    pages.push(list.slice(i, i + OTHER_PAGE_SIZE));
                }
            }

            if (pages.length === 0) pages.push([]);

            return pages;
        }
    },
    methods: {
        getProjectAt(page, rowIdx, colIdx, pageIndex) {
            if (!page) return null;

            if (pageIndex === 0) {
                if (rowIdx === 1) {
                    return colIdx === 2 ? page[0] : null;
                } else {
                    const index = (rowIdx - 2) * 3 + colIdx + 1;
                    return page[index];
                }
            } else {
                const index = (rowIdx - 1) * 3 + colIdx;
                return page[index];
            }
        },
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 800;
            fCanvas.height = 300;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 300, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 800, 300);
            this.footerImage = fCanvas.toDataURL();
        }
    },
    mounted() {
        this.generateGradients();
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': projects.style.colorScheme.header,
        '--body-color': projects.style.colorScheme.body,
        '--footer-color': projects.style.colorScheme.footer,
        '--heading-color': projects.style.heading.color,
        '--heading-size': projects.style.heading.size + 'px',
        '--heading-font': projects.style.heading.font,
        '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': projects.style.heading.underline ? 'underline' : 'none',
        '--label-color': projects.style.label.color,
        '--label-size': projects.style.label.size + 'px',
        '--label-font': projects.style.label.font,
        '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
        '--label-style': projects.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': projects.style.label.underline ? 'underline' : 'none',
        '--text-color': projects.style.text.color,
        '--text-size': projects.style.text.size + 'px',
        '--text-font': projects.style.text.font,
        '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
        '--text-style': projects.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
    }">
        <div v-for="(page, pageIndex) in projectPages" :key="pageIndex" class="sub-container page-unit">
            <!-- HEADER -->
            <div class="header-banner banner">
                <div class="header-content">
                    <h2 class="header-title">
                        <SpellCheckedText :value="projects.title" @input="projects.title = $event" />
                    </h2>
                    <div class="logo-wrapper" v-if="projects.logo">
                        <div class="header-logo photobox">
                            <PhotoBox :project="projects" type="logo" bgColor="transparent" objectFit="contain" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="page-content">
                <!-- 4 Rows Layout -->
                <div v-for="rowIdx in 4" :key="'row-' + rowIdx" class="content-row" :class="'row-' + rowIdx">

                    <!-- Column 0 -->
                    <!-- Special Case: About Box in Row 1, Col 0 -->
                    <div v-if="pageIndex === 0 && rowIdx === 1" class="about-box">
                        <div class="about-content">
                            <p class="about-title">
                                <SpellCheckedText :value="projects.about && projects.about.title"
                                    @input="projects.about.title = $event" />
                            </p>
                            <p class="about-description">
                                <SpellCheckedText :value="projects.about && projects.about.description"
                                    @input="projects.about.description = $event" />
                            </p>
                        </div>
                    </div>
                    <!-- Standard Project Item Col 0 (if not about box) -->
                    <div v-else-if="getProjectAt(page, rowIdx, 0, pageIndex)" class="project-item"
                        :key="'proj-' + rowIdx + '-0'">
                        <PhotoBox class="photobox" :project="getProjectAt(page, rowIdx, 0, pageIndex)" :index="0"
                            height="100%" :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                        <div class="project-overlay">
                            <p class="proj-name">{{ (getProjectAt(page, rowIdx, 0, pageIndex) || {}).projectName }}</p>
                            <p class="proj-loc">
                                <template>
                                    {{ getProjectAt(page, rowIdx, 0, pageIndex).city || 'N/A' }}, {{ getProjectAt(page,
                                        rowIdx,
                                        0, pageIndex).stateProvince || 'N/A' }}
                                </template>
                            </p>
                        </div>
                        <div v-if="footerImage" class="bottom-gradient">
                            <img :src="footerImage" class="w-full h-full" />
                        </div>
                    </div>
                    <div v-else class="project-item placeholder-item" :key="'place-' + rowIdx + '-0'" />

                    <!-- Column 1 -->
                    <div v-if="!(pageIndex === 0 && rowIdx === 1)" class="column-wrapper" style="display: contents;">
                        <div v-if="getProjectAt(page, rowIdx, 1, pageIndex)" class="project-item"
                            :key="'proj-' + rowIdx + '-1'">
                            <PhotoBox class="photobox" :project="getProjectAt(page, rowIdx, 1, pageIndex)" :index="0"
                                :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                            <div class="project-overlay">
                                <p class="proj-name">{{ (getProjectAt(page, rowIdx, 1, pageIndex) || {}).projectName ||
                                    'Project Name' }}</p>
                                <p class="proj-loc">
                                    <template v-if="(getProjectAt(page, rowIdx, 1, pageIndex) || {}).city">
                                        {{ getProjectAt(page, rowIdx, 1, pageIndex).city }}, {{ getProjectAt(page,
                                            rowIdx, 1, pageIndex).stateProvince }}
                                    </template>
                                </p>
                            </div>
                            <div v-if="footerImage" class="bottom-gradient">
                                <img :src="footerImage" class="w-full h-full" />
                            </div>
                        </div>
                        <div v-else class="project-item placeholder-item" :key="'place-' + rowIdx + '-1'" />
                    </div>

                    <!-- Column 2 -->
                    <div v-if="getProjectAt(page, rowIdx, 2, pageIndex)" class="project-item"
                        :key="'proj-' + rowIdx + '-2'">
                        <PhotoBox class="photobox" :project="getProjectAt(page, rowIdx, 2, pageIndex)" :index="0"
                            height="100%" :bgColor="projects.style.colorScheme && projects.style.colorScheme.body" />
                        <div class="project-overlay">
                            <p class="proj-name">{{ (getProjectAt(page, rowIdx, 2, pageIndex) || {}).projectName }}</p>
                            <p class="proj-loc">
                                <template v-if="(getProjectAt(page, rowIdx, 2, pageIndex) || {}).city">
                                    {{ getProjectAt(page, rowIdx, 2, pageIndex).city }}
                                    <span
                                        v-if="getProjectAt(page, rowIdx, 2, pageIndex).city && getProjectAt(page, rowIdx, 2, pageIndex).stateProvince">,
                                    </span>
                                    {{ getProjectAt(page, rowIdx, 2, pageIndex).stateProvince }}
                                </template>
                            </p>
                        </div>
                        <div v-if="footerImage" class="bottom-gradient">
                            <img :src="footerImage" class="w-full h-full" />
                        </div>
                    </div>
                    <div v-else class="project-item placeholder-item" :key="'place-' + rowIdx + '-2'" />
                </div>
            </div>

            <!-- FOOTER -->
            <div class="footer-bottom">
                <div class="footer-content">
                    <div class="footer-left">
                        <div class="footer-line" />
                        <p class="footer-text">
                            <SpellCheckedText :value="projects.opprName" @input="projects.opprName = $event" />
                        </p>
                    </div>
                    <div class="footer-right">
                        <div class="page-badge">Page {{ startPage + pageIndex }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;
    background: #f5f5f5;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: column;
        background: white;
        height: 296.83mm;
        width: 211mm;
        position: relative;
        overflow: hidden;
        padding-bottom: 40px;
    }

    .banner {
        height: 100px;
        display: flex;
        align-items: center;
        padding: 0 50px;

        &.header-banner {
            background: var(--header-color);
        }
    }

    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .header-title {
            color: white;
            font-size: 16px;
            font-weight: 700;
            margin: 0;
            font-family: Helvetica;
        }

        .header-logo {
            height: 80px;
            width: 120px;
        }
    }

    .page-content {
        flex: 1;
        padding: 80px 60px;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .content-row {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            width: 100%;

            &.row-1 {
                gap: 30px;
                align-items: center;
                margin-bottom: 20px;
            }

            &.row-2 {
                gap: 10px;
                margin-bottom: 70px;
            }

            &.row-3 {
                gap: 10px;
                margin-bottom: 40px;
            }

            &.row-4 {
                gap: 10px;
                margin-bottom: 0;
            }
        }
    }

    .about-box {
        grid-column: span 2;
        background: var(--body-color);
        padding: 20px;
        display: flex;
        justify-content: center;
        text-align: center;
        height: 250px;
        overflow: hidden;

        .about-title {
            font-size: var(--label-size);
            font-weight: var(--label-weight);
            font-style: var(--label-style);
            font-family: var(--label-font);
            color: var(--label-color);
            text-decoration: var(--label-decoration);
            margin-bottom: 5px;
        }

        .about-description {
            font-size: var(--text-size);
            font-weight: var(--text-weight);
            font-style: var(--text-style);
            font-family: var(--text-font);
            color: var(--text-color);
            text-decoration: var(--text-decoration);
            margin: 0;
        }
    }

    .project-item {
        height: 150px;
        position: relative;
        overflow: hidden;
        background: #eee;

        .project-overlay,
        .placeholder-overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 15px 12px 10px;
            pointer-events: none;
            z-index: 5;

            .proj-name {
                font-size: 10px;
                font-weight: bold;
                font-style: italic;
                font-family: Helvetica;
                color: white;
                margin: 0;
                line-height: 1.2;
                margin-bottom: 2px;
            }

            .proj-loc {
                font-size: 10px;
                font-weight: bold;
                font-style: italic;
                font-family: Helvetica;
                color: white;
                margin: 0;
            }
        }

        .bottom-gradient {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 120px;
            z-index: 2;
            pointer-events: none;
        }
    }

    .placeholder-item {
        background: var(--body-color);
    }

    .footer-bottom {
        margin-top: auto;

        .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .footer-left {
                flex: 1;
                margin: 0 20px 0px 50px;

                .footer-line {
                    height: 2px;
                    background: var(--footer-color);
                }

                .footer-text {
                    margin-top: 10px;
                    margin-bottom: 0px;
                    font-size: 14px;
                    color: black;
                    font-family: Helvetica;
                }
            }

            .footer-right {
                .page-badge {
                    background: var(--footer-color);
                    color: white;
                    padding: 8px 80px 8px 30px;
                    font-size: 12px;
                    font-weight: 700;
                }
            }
        }
    }
}
</style>
